import { useMemo, useState, useCallback } from 'react';
import { useSubmissions } from '@/hooks/useSubmissions';
import { useUsers } from '@/hooks/useUsers';
import { sectors } from '@/mocks/formData';
import type { DashboardStats } from '@/types';

export type PeriodFilter = '7d' | '30d' | '90d' | '12m' | 'all';

export interface DashboardFilters {
  period: PeriodFilter;
  setor: string;
  status: string;
}

function filterByPeriod(date: string, period: PeriodFilter): boolean {
  if (period === 'all') return true;
  const now = new Date();
  const itemDate = new Date(date);
  const diffMs = now.getTime() - itemDate.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  switch (period) {
    case '7d': return diffDays <= 7;
    case '30d': return diffDays <= 30;
    case '90d': return diffDays <= 90;
    case '12m': return diffDays <= 365;
    default: return true;
  }
}

export function useDashboard() {
  const { epiRequests, incidents, denunciations, loading, error } = useSubmissions();
  const { totalUsers } = useUsers();
  const [filters, setFilters] = useState<DashboardFilters>({
    period: '30d',
    setor: 'all',
    status: 'all',
  });

  const updateFilter = useCallback((key: keyof DashboardFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const filteredEPIs = useMemo(() => {
    return epiRequests.filter((epi) => {
      if (!filterByPeriod(epi.createdAt, filters.period)) return false;
      if (filters.setor !== 'all' && epi.setor !== filters.setor) return false;
      if (filters.status !== 'all' && epi.status !== filters.status) return false;
      return true;
    });
  }, [epiRequests, filters]);

  const filteredIncidents = useMemo(() => {
    return incidents.filter((inc) => {
      if (!filterByPeriod(inc.createdAt, filters.period)) return false;
      if (filters.setor !== 'all' && inc.setor !== filters.setor) return false;
      return true;
    });
  }, [incidents, filters]);

  const filteredDenunciations = useMemo(() => {
    return denunciations.filter((den) => {
      if (!filterByPeriod(den.createdAt, filters.period)) return false;
      if (filters.setor !== 'all' && den.setor !== filters.setor) return false;
      return true;
    });
  }, [denunciations, filters]);

  const stats: DashboardStats = useMemo(() => {
    return {
      totalColaboradores: totalUsers,
      totalSolicitacoesEPI: filteredEPIs.length,
      solicitacoesPendentes: filteredEPIs.filter((e) => e.status === 'Pendente').length,
      solicitacoesAprovadas: filteredEPIs.filter((e) => e.status === 'Aprovado').length,
      solicitacoesNegadas: filteredEPIs.filter((e) => e.status === 'Negado').length,
      incidentesRegistrados: filteredIncidents.length,
      denunciasRecebidas: filteredDenunciations.length,
    };
  }, [filteredEPIs, filteredIncidents, filteredDenunciations, totalUsers]);

  const epiBySector = useMemo(() => {
    const map: Record<string, number> = {};
    filteredEPIs.forEach((epi) => {
      map[epi.setor] = (map[epi.setor] || 0) + 1;
    });
    return sectors.filter((s) => map[s]).map((s) => ({ name: s, count: map[s] }));
  }, [filteredEPIs]);

  const epiByStatus = useMemo(() => {
    return [
      { name: 'Pendentes', value: stats.solicitacoesPendentes, color: '#F59E0B' },
      { name: 'Aprovados', value: stats.solicitacoesAprovadas, color: '#10B981' },
      { name: 'Negados', value: stats.solicitacoesNegadas, color: '#EF4444' },
    ];
  }, [stats]);

  const epiByType = useMemo(() => {
    const map: Record<string, number> = {};
    filteredEPIs.forEach((epi) => {
      epi.itens.forEach((item) => {
        const total = item.q01 + item.q02 + item.qCaixa;
        map[item.tipo] = (map[item.tipo] || 0) + total;
      });
    });
    return Object.entries(map)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [filteredEPIs]);

  const monthlyData = useMemo(() => {
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    const currentYear = new Date().getFullYear();
    const data = months.map((m, i) => {
      const month = String(i + 1).padStart(2, '0');
      const prefix = `${currentYear}-${month}`;
      return {
        name: m,
        epi: epiRequests.filter((e) => e.createdAt.startsWith(prefix)).length,
        incidentes: incidents.filter((inc) => inc.createdAt.startsWith(prefix)).length,
        denuncias: denunciations.filter((d) => d.createdAt.startsWith(prefix)).length,
      };
    });
    return data;
  }, [epiRequests, incidents, denunciations]);

  const trendData = useMemo(() => {
    let epiCum = 0;
    let incCum = 0;
    let denCum = 0;
    return monthlyData.map((m) => {
      epiCum += m.epi;
      incCum += m.incidentes;
      denCum += m.denuncias;
      return { name: m.name, epi: epiCum, incidentes: incCum, denuncias: denCum };
    });
  }, [monthlyData]);

  const incidentsByType = useMemo(() => {
    const map: Record<string, number> = {};
    filteredIncidents.forEach((inc) => {
      map[inc.tipoIncidente] = (map[inc.tipoIncidente] || 0) + 1;
    });
    return Object.entries(map)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [filteredIncidents]);

  const denunciationsByLevel = useMemo(() => {
    const buckets: Record<string, number> = {
      'Baixo (0-3)': 0,
      'Médio (4-6)': 0,
      'Alto (7-9)': 0,
    };
    filteredDenunciations.forEach((d) => {
      if (d.nivelAssedio <= 3) buckets['Baixo (0-3)'] += 1;
      else if (d.nivelAssedio <= 6) buckets['Médio (4-6)'] += 1;
      else buckets['Alto (7-9)'] += 1;
    });
    return Object.entries(buckets).map(([name, count]) => ({ name, count }));
  }, [filteredDenunciations]);

  const denunciationsBySector = useMemo(() => {
    const map: Record<string, number> = {};
    filteredDenunciations.forEach((d) => {
      map[d.setor] = (map[d.setor] || 0) + 1;
    });
    return sectors.filter((s) => map[s]).map((s) => ({ name: s, count: map[s] }));
  }, [filteredDenunciations]);

  const recentSubmissions = useMemo(() => {
    const all = [
      ...filteredEPIs.map((e) => ({ ...e, type: 'epi' as const })),
      ...filteredIncidents.map((e) => ({ ...e, type: 'incident' as const })),
      ...filteredDenunciations.map((e) => ({ ...e, type: 'denunciation' as const })),
    ];
    return all.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 10);
  }, [filteredEPIs, filteredIncidents, filteredDenunciations]);

  return {
    filters,
    updateFilter,
    stats,
    epiBySector,
    epiByStatus,
    epiByType,
    monthlyData,
    trendData,
    incidentsByType,
    denunciationsByLevel,
    denunciationsBySector,
    recentSubmissions,
    filteredEPIs,
    filteredIncidents,
    filteredDenunciations,
    loading,
    error,
  };
}